import _ from 'lodash'
import type {
    SchemaService,
    ComponentFeature,
    DataItemsMap,
    ResolvedDataItem,
    RawComponentExport,
    RawComponentExportStructure,
    NamespacesDataMap,
    DalValue,
    Namespace,
    DependenciesManager
} from '@wix/document-services-types'
import {
    DetailedValue,
    type ExtendedRefInfo,
    type ReferredDataItemsIterateeContext,
    forEachReferredDataItem,
    getDetailedValueByPath
} from '@wix/import-export-utils'
import {COMPONENT_FEATURES} from '../../import-export/json/constants'
import {stripHashIfExists} from '../../../utils/refArrayUtils'
import {VIEW_MODES} from '../../../constants/constants'
import {getDependenciesManager} from '../../import-export/json/raw/utils/dependencies'

const reuseId = (id: string | undefined, type: Namespace, {references}: DependenciesManager): void => {
    if (id) {
        const strippedId = stripHashIfExists(id)
        references.addReference(strippedId, type)
    }
}

const reuseDataItemIds = (
    dataItem: DalValue,
    namespace: Namespace,
    refFieldsInfo: ExtendedRefInfo[],
    dependenciesManager: DependenciesManager
) => {
    reuseId(dataItem.id, namespace, dependenciesManager)

    for (const {isRefOwner, path, referencedMap} of refFieldsInfo) {
        if (isRefOwner) {
            continue
        }

        const valueByPath: DetailedValue[] = getDetailedValueByPath(dataItem, path)
        for (const {value} of valueByPath) {
            reuseId(value, referencedMap, dependenciesManager)
        }
    }
}

const reuseDataItemsIds = (
    dataItems: NamespacesDataMap | undefined,
    dependenciesManager: DependenciesManager,
    schemaService: SchemaService
): void => {
    _.forOwn(dataItems, (namespaceItems: DataItemsMap, namespace: string) => {
        _.forOwn(namespaceItems, (dataItem: ResolvedDataItem, dataId: string) => {
            forEachReferredDataItem(
                schemaService,
                {id: dataId, ...dataItem},
                namespace,
                ({item, namespace: referencedMap, refFieldsInfo}: ReferredDataItemsIterateeContext) =>
                    reuseDataItemIds(item, referencedMap, refFieldsInfo, dependenciesManager)
            )
        })
    })
}

const reuseComponentIds = (
    component: RawComponentExportStructure,
    dependenciesManager: DependenciesManager,
    schemaService: SchemaService
): void => {
    const {structure, data, overrides} = component

    reuseId(structure.id, VIEW_MODES.DESKTOP, dependenciesManager)

    _.forOwn(COMPONENT_FEATURES, ({namespace, alias}: ComponentFeature) => {
        const dataItem = data[alias]
        if (!dataItem) {
            return
        }

        forEachReferredDataItem(
            schemaService,
            dataItem,
            namespace,
            ({item, namespace: referencedMap, refFieldsInfo}: ReferredDataItemsIterateeContext) =>
                reuseDataItemIds(item, referencedMap, refFieldsInfo, dependenciesManager)
        )
    })

    reuseDataItemsIds(overrides, dependenciesManager, schemaService)
}

export const reuseIds = (componentExport: RawComponentExport, schemaService: SchemaService): void => {
    const {components, dependencies, globalData} = componentExport
    const dependenciesManager: DependenciesManager = getDependenciesManager(dependencies)

    _.forOwn(components, component => {
        reuseComponentIds(component, dependenciesManager, schemaService)
    })

    reuseDataItemsIds(globalData, dependenciesManager, schemaService)

    componentExport.dependencies = dependenciesManager.getDependencies()
}
